import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PlayerCardProps } from './types';
import { formatDateString as formatDate } from '../../lib/utils';

const getCardStyles = (status: PlayerCardProps['status']) => {
  switch (status) {
    case 'offered':
      return "bg-yellow-50 border-yellow-200";
    case 'rostered':
      return "bg-green-50 border-green-200";
    case 'considering':
    default:
      return ""; // Default border styles
  }
};

import { Info, StickyNote, UserCheck, UserX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PlayerCard = ({
  teamPlayer,
  status,
  onOfferSpot,
  onRescindOffer,
  onAcceptOffer,
  onDeclineOffer,
  onRemoveFromRoster,
  onRemovePlayer,
  // onOpenNotes, // Remove dialog/modal-related prop
  hasNotes,
  compact = false,
}: PlayerCardProps & { compact?: boolean }) => {
  const navigate = useNavigate();
  const { id: teamPlayerId, player, updated_at } = teamPlayer;
  const { id: playerId, first_name, last_name, birthdate } = player;

  if (compact) {
    // Compact grid row layout
    const positions = player.profile?.positions || [];
    const competitiveness = player.profile?.competitiveness || [];
    // Color coding for compact mode
    let statusColor = '';
    if (status === 'offered') statusColor = 'bg-yellow-50 border-yellow-200';
    else if (status === 'rostered') statusColor = 'bg-green-50 border-green-200';
    else statusColor = '';
    return (
      <div className={`flex items-center border rounded-md px-2 py-1 hover:shadow-sm transition-shadow min-h-[56px] ${statusColor}`}> 
        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
          <div className="truncate font-medium text-sm">
            {first_name} {last_name}
            {hasNotes && (
              <StickyNote className="inline ml-1 h-4 w-4 text-yellow-500 align-text-bottom cursor-pointer" onClick={() => onOpenNotes(teamPlayerId, playerId)} />
            )}
          </div>
          <div className="text-xs text-gray-500 truncate">
            {birthdate ? formatDate(birthdate) : 'No birthdate'}
          </div>
          <div className="text-xs text-gray-500 mt-0.5 truncate">
            {[...positions, ...competitiveness].join(', ')}
          </div>
        </div>
        <div className="flex gap-1 items-center ml-2">
          {/* Considering: Offer Spot and Remove */}
          {status === 'considering' && onOfferSpot && (
            <Button variant="ghost" size="icon" className="text-green-600" aria-label="Offer Spot" onClick={() => onOfferSpot(teamPlayerId, playerId)}>
              <UserCheck className="h-5 w-5" />
            </Button>
          )}
          {/* Offered: Accept Offer and Remove (Back to Consider) */}
          {status === 'offered' && onAcceptOffer && (
            <Button variant="ghost" size="icon" className="text-green-600" aria-label="Accept Offer" onClick={() => onAcceptOffer(teamPlayerId, playerId)}>
              <UserCheck className="h-5 w-5" />
            </Button>
          )}
          {/* Always: View Details */}
          <Button variant="ghost" size="icon" aria-label="View Details" onClick={() => navigate(`/player/${playerId}/${teamPlayer.team_id}`)}>
            <Info className="h-5 w-5" />
          </Button>
          {/* Offered: Decline Offer */}
          {status === 'offered' && onDeclineOffer && (
            <Button variant="ghost" size="icon" className="text-red-600" aria-label="Decline Offer" onClick={() => onDeclineOffer(teamPlayerId, playerId)}>
              <UserX className="h-5 w-5" />
            </Button>
          )}
          {/* Declined: Remove (Back to Consider) */}
          {status === 'declined' && onRescindOffer && (
            <Button variant="ghost" size="icon" className="text-orange-600" aria-label="Remove (Back to Consider)" onClick={() => {
              if (window.confirm('Are you sure you want to move this player back to the considering status?')) {
                onRescindOffer(teamPlayerId, playerId);
              }
            }}>
              <UserX className="h-5 w-5" />
            </Button>
          )}
          {/* Rostered: Remove (Back to Consider) */}
          {status === 'rostered' && onRemoveFromRoster && (
            <Button variant="ghost" size="icon" className="text-red-500" aria-label="Remove (Back to Consider)" onClick={() => {
              if (window.confirm('Are you sure you want to move this player back to the considering status?')) {
                onRemoveFromRoster(teamPlayerId, playerId);
              }
            }}>
              <UserX className="h-5 w-5" />
            </Button>
          )}
          {/* Considering: Remove */}
          {status === 'considering' && onRemovePlayer && (
            <Button variant="ghost" size="icon" className="text-red-500" aria-label="Remove" onClick={() => {
              if (window.confirm('Are you sure you want to remove this player from being considered?')) {
                onRemovePlayer(teamPlayerId, playerId);
              }
            }}>
              <UserX className="h-5 w-5" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Default (full) layout
  return (
    <div className={`p-3 border rounded-md ${getCardStyles(status)}`}>
      {/* Wrapper for main row content */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2">
        {/* Player Info */}
        <div className="flex-grow">
          <div className="font-medium">{first_name} {last_name}</div>
          <div className="text-sm text-gray-500">
            {birthdate ? formatDate(birthdate) : 'No birthdate'}
          </div>

          {/* Position and competitiveness pillboxes */}
          <div className="flex flex-wrap gap-1 mt-1 items-center">
            {player.profile?.positions?.map(pos => (
              <Badge key={pos} variant="default" className="text-xs font-normal">
                {pos}
              </Badge>
            ))}
            {player.profile?.competitiveness?.map(comp => (
              <Badge key={comp} variant="outline" className="text-xs font-normal">
                {comp}
              </Badge>
            ))}
            {hasNotes && <Badge variant="default" className="text-xs font-normal">Has Notes</Badge>}
          </div>
        </div>
        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 items-center justify-end shrink-0">
          {status === 'considering' && onOfferSpot && (
            <Button
              variant="outline"
              size="sm"
              className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
              onClick={() => onOfferSpot(teamPlayerId, playerId)}
            >
              Offer Spot
            </Button>
          )}
          {status === 'offered' && onAcceptOffer && (
            <Button
              variant="outline"
              size="sm"
              className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
              onClick={() => onAcceptOffer(teamPlayerId, playerId)}
            >
              Accept Offer
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/player/${playerId}/${teamPlayer.team_id}`)}
          >
            View Details
          </Button>
          {status === 'offered' && onRescindOffer && (
            <Button
              variant="outline"
              size="sm"
              className="text-orange-600 hover:bg-orange-50 hover:text-orange-700 border-orange-200"
              onClick={() => {
  if (window.confirm('Are you sure you want to move this player back to the considering status?')) {
    onRescindOffer(teamPlayerId, playerId);
  }
}}
            >
              Remove (Back to Consider)
            </Button>
          )}
          {status === 'rostered' && onRemoveFromRoster && (
            <Button
              variant="outline"
              size="sm"
              className="text-red-500 hover:bg-red-50 hover:text-red-600"
              onClick={() => {
  if (window.confirm('Are you sure you want to move this player back to the considering status?')) {
    onRemoveFromRoster(teamPlayerId, playerId);
  }
}}
            >
              Remove (Back to Consider)
            </Button>
          )}
          {status === 'considering' && onRemovePlayer && (
            <Button
              variant="outline"
              size="sm"
              className="text-red-500 hover:bg-red-50 hover:text-red-600"
              onClick={() => {
  if (window.confirm('Are you sure you want to remove this player from being considered?')) {
    onRemovePlayer(teamPlayerId, playerId);
  }
}}
            >
              Remove
            </Button>
          )}
        </div>
      </div>
      {/* Timestamp - Placed outside the main row wrapper */}
      {updated_at && (
        <div className="w-full text-right text-xs text-gray-400 mt-2 pt-1 border-t border-gray-100">
          Status Last Changed: {formatDate(updated_at, 'MM/dd/yyyy hh:mm a')}
        </div>
      )}
    </div>
  );
};

export default PlayerCard;